import _ from 'lodash'
import { ActionTree } from 'vuex'
import { RootState, MenuItem, DashButton } from '@/store/types'
import client from '@/base-http'
import { RouteConfig } from 'vue-router'
import KeyValuePair from '@/modules/core/support/KeyValuePair'
import { addRoutes } from '@/router'
import { Config } from '@/modules/core/config'
import themes from '@/themes'
import {CheckAuthorizationResult} from '@/modules/authorization/model/CheckAuthorizationResult'
import {PrologueModule} from '@/types'

/**
 * Filtra la rutas
 * @param routes
 * @param modules
 */
function filterRoutes (routes: RouteConfig[], modules: KeyValuePair) {
    return routes.filter(route => {
        const moduleName: string = _.get(route, 'meta.module', '')
        // Si no declara un módulo, la ruta es cargada por defecto
        if (!moduleName) {
            return true
        }

        // Si la ruta pertenece a un módulo habilitado, se carga y ademas se filtran
        // sus hijos
        if (moduleIsEnabled(moduleName.split('.'), modules)) {
            if (route.children && route.children.length) {
                route.children = filterRoutes(route.children, modules)
            }

            return true
        }

        return false
    })
}

/**
 * Comprueba si un módulo esta habilitado
 *
 * @param modulePaths
 * @param modules
 */
function moduleIsEnabled (modulePaths: string[], modules: KeyValuePair): boolean {
    if (!modulePaths) {
        return false
    }

    const pathCount    = modulePaths.length
    const currentPath  = modulePaths.shift()!
    const currentValue = modules[currentPath]

    if (pathCount === 1 && currentValue) {
        return true
    }

    if (_.isObject(currentValue)) {
        return moduleIsEnabled(modulePaths, currentValue)
    }

    return !!currentValue
}

const modules: {[key: string]: () => Promise<PrologueModule>} = {
    'i18n':  () => import('@/modules/i18n'),
    'reservation': () => import('@/modules/reservation'),
    'gallery': () => import('@/modules/cms/modules/gallery'),
    'calendar': () => import('@/modules/calendar'),
    'user': () => import('@/modules/user'),
    'authorization': () => import('@/modules/authorization'),
    'blog': () => import('@/modules/blog'),
    'shipping': () => import('@/modules/shipping'),
    'media': () => import('@/modules/media'),
    'cms': () => import('@/modules/cms'),
    'payment': () => import('@/modules/payment'),
    'product': () => import('@/modules/product'),
    'app-router': () => import('@/modules/app-router'),
    'support': () => import('@/modules/support'),
}

const actions: ActionTree<RootState, RootState> = {
    /**
     * Cargar módulos de la aplicación
     *
     * Solicita al servidor la lista de módulo habilitados para la instancia de la aplicación.
     * Aquellos módulos que se encuentren activos serán procesados para añadir sus
     * correspondientes rutas de navegación y menús de navegación.
     *
     * @param rootState
     * @param state
     * @param commit
     * @param dispatch
     * @param payload
     */
    async loadModules ({rootState, state, commit, dispatch}, payload: {force: boolean}) {
        if (!rootState.auth!.token) {
            return
        }

        // Ya se ha obtenido la lista de módulos
        if (state.enabledModulesFetched && !payload.force) {
            return
        }

        const enabledModules = (await client.get('prologue/modules')).data as string[]
        const appMenu: MenuItem[] = []

        for (const moduleName of enabledModules) {
            if (!(moduleName in modules)) {
                throw new Error(`El módulo ${moduleName} se ha habilitado en el servidor pero no existe en la app`)
            }

            const module = await modules[moduleName]()
            const { routes, menu } = module
            // Registra en la aplicación menus y rutas si el módulo declarada las funciones
            // para registrarlas
            // tslint:disable-next-line:no-unused-expression
            routes && addRoutes(routes())
            // tslint:disable-next-line:no-unused-expression
            menu && appMenu.push(...menu())

        }

        commit('setEnabledModules', enabledModules)
        commit('toggleEnabledModulesFetched', true)
        await dispatch('loadMenuItems', appMenu)
    },

    /**
     * Carga los elementos del menú principal
     *
     * @param param0
     * @param items
     */
    async loadMenuItems ({commit, state, dispatch}, items: MenuItem[]) {
        const collect = (items: MenuItem[]) => {
            const result: string[] = []
            items.forEach(m => {
                if (m.permission) {
                    result.push(m.permission)
                    if (m.children && Array.isArray(m.children) && m.children.length) {
                        result.push(...collect(m.children))
                    }
                }
            })

            return _.uniq(result)
        }

        const menuPermissions = collect(items)

        const status: CheckAuthorizationResult = await dispatch(
            'authorization/checkAuthorization',
            menuPermissions,
            { root: true },
        )

        // Filtra los elementos del menu y deja solo aquellos que a los que el usuario puede acceder
        const filterMenu = (items: MenuItem[], status: CheckAuthorizationResult) => {
            return items.filter(item => {
                if (!item.requireAuthorization) {
                    return true
                }

                if (item.requireAuthorization && status.isGranted(item.permission || '')) {
                    item.children = filterMenu(item.children || [], status)

                    return true
                }

                return false
            })
        }

        commit('setMenuItems', filterMenu(items, status))
    },

    /**
     * Elimina la configuración de acceso a los módulos de la aplicación
     */
    async clearModuleConfig ({commit}) {
        commit('setEnabledModules', {})
        commit('toggleEnabledModulesFetched', false)
        commit('setMenuItems', [])
    },

    /**
     * Carga en el estado de la aplicación la configuración del panel obtenida del servidor
     * @param param0
     */
    async loadConfig ({rootState, commit}) {
        // No existe token de autorización, no se puede obtener la configuración
        if (!rootState.auth!.token) {
            return
        }

        const data = (await client.get('prologue/config')).data
        commit('setConfig', Config.deserialize(data))
    },

    async loadStyleConfig ({commit}) {
        commit('setThemes', themes)

        // Establece el tema de colores si existe alguno guardado
        const themeKey = localStorage.getItem('config.theme')
        if (themeKey) {
            const theme = themes.find(theme => theme.key === themeKey)
            if (theme) {
                 commit('setCurrentTheme', theme)
            }
        }

        // Establece el estilo de inputs
        const inputStyle = localStorage.getItem('config.inputStyle')
        if (inputStyle) {
            commit('setInputStyle', inputStyle)
        }
    },
}

export default actions
